import { useCallback, useMemo, useState, type MouseEvent } from "react";
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import {
  Toolbar,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  QuickFilter,
  QuickFilterControl,
  useGridApiContext,
  useGridSelector,
  gridFilterActiveItemsSelector,
} from "@mui/x-data-grid-pro";
import { DataGridTemplate } from "./dataGridTypes";
import { ListRestart } from "lucide-react";

export interface ToolbarTemplate extends DataGridTemplate {
  isServerTemplate: boolean;
}

interface TemplateToolbarProps {
  templates: ToolbarTemplate[];
  activeTemplateId: number | null;
  showReset: boolean;
  isLoadingTemplates: boolean;
  isSavingTemplate: boolean;
  isDeletingTemplate: boolean;
  togglingStarTemplateId: number | null;
  onSaveTemplate: (name?: string) => Promise<void> | void;
  onLoadTemplate: (template: ToolbarTemplate) => void;
  onDeleteTemplate: (template: ToolbarTemplate) => Promise<void> | void;
  onToggleTemplateStar: (template: ToolbarTemplate) => Promise<void> | void;
  onReset: () => void;
  disableSaving: boolean;
  csvOptions?: {
    delimiter?: string;
    utf8WithBom?: boolean;
  };
  quickFilterProps?: {
    debounceMs?: number;
  };
}

const FilterButtonWithIndicator = () => {
  const apiRef = useGridApiContext();
  const activeFilters = useGridSelector(apiRef, gridFilterActiveItemsSelector);
  const activeFilterCount = activeFilters.length;

  return (
    <FilterPanelTrigger
      render={(props) => (
        <Button
          {...props}
          variant="text"
          size="small"
          startIcon={
            <Badge color="primary" badgeContent={activeFilterCount} invisible={activeFilterCount === 0}>
              <FilterListOutlinedIcon />
            </Badge>
          }
        >
          Filter
        </Button>
      )}
    />
  );
};

const ExpandableGridQuickSearch = ({ debounceMs = 300 }: { debounceMs?: number }) => {
  const apiRef = useGridApiContext();
  const quickFilterValues = apiRef.current.state.filter.filterModel.quickFilterValues ?? [];
  const hasValue = quickFilterValues.some((value) => String(value ?? "").length > 0);

  const [open, setOpen] = useState(hasValue);

  const handleClose = () => {
    apiRef.current.setQuickFilterValues([]);
    setOpen(false);
  };

  if (!open) {
    return (
      <Button variant="text" size="small" startIcon={<SearchOutlinedIcon />} onClick={() => setOpen(true)}>
        Suche
      </Button>
    );
  }

  return (
    <QuickFilter defaultExpanded>
      <QuickFilterControl
        debounceMs={debounceMs}
        render={(props) => (
          <TextField
            {...props}
            size="small"
            placeholder="Suchen..."
            sx={{
              width: 260,
              transition: "width 0.2s ease",
            }}
            InputProps={{
              ...props.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClose} aria-label="Suche schließen">
                    <ClearOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </QuickFilter>
  );
};

export const TemplateToolbar = ({
  templates,
  activeTemplateId,
  showReset,
  isLoadingTemplates,
  isSavingTemplate,
  isDeletingTemplate,
  togglingStarTemplateId,
  onSaveTemplate,
  onLoadTemplate,
  onDeleteTemplate,
  onToggleTemplateStar,
  onReset,
  disableSaving,
  csvOptions,
  quickFilterProps,
}: TemplateToolbarProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<ToolbarTemplate | null>(null);
  const [templateName, setTemplateName] = useState("");

  const isMenuOpen = Boolean(menuAnchorEl);
  const hasTemplates = templates.length > 0;
  const hasActiveTemplate = activeTemplateId !== null;

  const menuTemplates = useMemo(() => templates, [templates]);
  const starredTemplates = useMemo(() => templates.filter((template) => template.starred), [templates]);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenSaveDialog = () => {
    handleCloseMenu();
    setSaveDialogOpen(true);
  };

  const handleCloseSaveDialog = () => {
    if (isSavingTemplate) return;
    setSaveDialogOpen(false);
    setTemplateName("");
  };

  const handleSaveTemplate = useCallback(async () => {
    await onSaveTemplate(templateName);
    setTemplateName("");
    setSaveDialogOpen(false);
  }, [onSaveTemplate, templateName]);

  const handleOpenDeleteDialog = (template: ToolbarTemplate) => {
    if (!template.isServerTemplate) return;
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeletingTemplate) return;
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!templateToDelete) return;

    await onDeleteTemplate(templateToDelete);
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  }, [onDeleteTemplate, templateToDelete]);

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
          gap: 1,
          px: 1,
          py: 0.5,
          flexWrap: "nowrap",
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexShrink: 0 }}>
          <Button
            variant="text"
            size="small"
            startIcon={
              <Badge color="primary" variant="dot" overlap="circular" invisible={!hasActiveTemplate}>
                <ViewListOutlinedIcon />
              </Badge>
            }
            onClick={handleOpenMenu}
          >
            Ansichten
          </Button>

          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 340,
                },
              },
            }}
          >
            <MenuItem onClick={handleOpenSaveDialog} disabled={disableSaving || isSavingTemplate}>
              <ListItemIcon>
                <AddOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Aktuelle Ansicht speichern</ListItemText>
            </MenuItem>

            <Divider />

            {isLoadingTemplates ? (
              <MenuItem disabled>
                <ListItemText>Ansichten werden geladen...</ListItemText>
              </MenuItem>
            ) : hasTemplates ? (
              menuTemplates.map((template) => {
                const isActive = template.id === activeTemplateId;
                const isTogglingStar = togglingStarTemplateId === template.id;

                return (
                  <MenuItem
                    key={template.id}
                    selected={isActive}
                    onClick={() => {
                      onLoadTemplate(template);
                      handleCloseMenu();
                    }}
                    sx={{
                      gap: 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <IconButton
                        edge="start"
                        size="small"
                        aria-label={template.starred ? `Stern für ${template.name} entfernen` : `Stern für ${template.name} setzen`}
                        disabled={isTogglingStar}
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleTemplateStar(template);
                        }}
                      >
                        {template.starred ? <StarRoundedIcon fontSize="small" /> : <StarOutlineRoundedIcon fontSize="small" />}
                      </IconButton>
                    </ListItemIcon>

                    <ListItemText
                      primary={template.name}
                      slotProps={{
                        primary: {
                          noWrap: true,
                          sx: {
                            fontWeight: isActive ? 700 : 400,
                          },
                        },
                      }}
                    />

                    {template.isServerTemplate ? (
                      <Tooltip title="Ansicht löschen">
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={`Ansicht ${template.name} löschen`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCloseMenu();
                            handleOpenDeleteDialog(template);
                          }}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box sx={{ width: 34, flexShrink: 0 }} />
                    )}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem disabled>
                <ListItemText>Keine gespeicherten Ansichten vorhanden</ListItemText>
              </MenuItem>
            )}

            {showReset ? (
              <>
                <Divider />
                <MenuItem
                  onClick={() => {
                    onReset();
                    handleCloseMenu();
                  }}
                >
                  <ListItemIcon>
                    <ListRestart size={18} />
                  </ListItemIcon>
                  <ListItemText>Ansicht zurücksetzen</ListItemText>
                </MenuItem>
              </>
            ) : null}
          </Menu>

          <Dialog open={saveDialogOpen} onClose={handleCloseSaveDialog} fullWidth maxWidth="xs">
            <DialogTitle>Aktuelle Ansicht speichern</DialogTitle>

            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                size="small"
                label="Name der Ansicht"
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseSaveDialog} disabled={isSavingTemplate}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveTemplate} disabled={disableSaving || isSavingTemplate} variant="contained">
                Speichern
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} fullWidth maxWidth="xs">
            <DialogTitle>Ansicht löschen</DialogTitle>

            <DialogContent>Möchtest du die Ansicht "{templateToDelete?.name ?? ""}" wirklich löschen?</DialogContent>

            <DialogActions>
              <Button onClick={handleCloseDeleteDialog} disabled={isDeletingTemplate}>
                Abbrechen
              </Button>
              <Button onClick={handleConfirmDelete} disabled={isDeletingTemplate} color="error" variant="contained">
                Löschen
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {starredTemplates.length > 0 ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              overflowX: "auto",
              overflowY: "hidden",
              px: 1,
              mx: 1,
              borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
              whiteSpace: "nowrap",
              scrollbarWidth: "thin",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: "max-content" }}>
              {starredTemplates.map((template) => {
                const isActive = template.id === activeTemplateId;

                return (
                  <Button
                    key={template.id}
                    size="small"
                    variant={isActive ? "contained" : "outlined"}
                    color={isActive ? "primary" : "inherit"}
                    onClick={() => onLoadTemplate(template)}
                    sx={{
                      flexShrink: 0,
                      fontWeight: 400,
                    }}
                  >
                    {template.name}
                  </Button>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0 }} />
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <ColumnsPanelTrigger
            render={(props) => (
              <Button {...props} variant="text" size="small" startIcon={<ViewColumnOutlinedIcon />}>
                Spalten
              </Button>
            )}
          />

          <FilterButtonWithIndicator />

          <ExportCsv
            options={csvOptions}
            render={(props) => (
              <Button {...props} variant="text" size="small" startIcon={<DownloadOutlinedIcon />}>
                Export
              </Button>
            )}
          />

          <ExpandableGridQuickSearch debounceMs={quickFilterProps?.debounceMs ?? 300} />
        </Box>
      </Box>
    </Toolbar>
  );
};
